import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import DynamicFormRenderer from "../dynamicFormRenderer";
import {
  Sparkles,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Check,
} from "lucide-react";
import GenioLogo from "../../assets/images/icons/logo.svg";
import { getReportForms } from "../../screens/content/patients/patientDetails/actions";
import { Form } from "../../interfaces/api/form";
import {
  AddReportModalProps,
  SimpleSelectProps,
  ReportOption,
} from "../../interfaces/api/report";

const SimpleSelect: React.FC<SimpleSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    function handleGlobalClick(e: MouseEvent) {
      const selectEl = wrapperRef.current;
      const dropdownEl = dropdownRef.current;
      const targetEl = e.target as Node;

      const isOutsideSelect = selectEl && !selectEl.contains(targetEl);
      const isOutsideDropdown = dropdownEl && !dropdownEl.contains(targetEl);

      if (isOutsideSelect && isOutsideDropdown) {
        setTimeout(() => closeDropdown(), 0);
      }
    }

    document.addEventListener("click", handleGlobalClick, true);
    return () => document.removeEventListener("click", handleGlobalClick, true);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDropdown();
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen, closeDropdown]);

  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const filteredOpts = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    const dropdownHeight = Math.min(240, filteredOpts.length * 36 + 60);

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top = buttonRect.bottom + 4;
    let left = buttonRect.left;
    const width = buttonRect.width;

    if (
      spaceBelow < dropdownHeight &&
      spaceAbove > dropdownHeight &&
      spaceAbove > spaceBelow
    ) {
      top = buttonRect.top - dropdownHeight;
    } else {
      top = buttonRect.bottom + 4;

      // Ensure at least 100px of the dropdown is visible
      const minVisibleHeight = Math.min(100, dropdownHeight);
      if (spaceBelow < minVisibleHeight && spaceAbove > minVisibleHeight) {
        top = buttonRect.top - dropdownHeight;
      }
    }

    // Ensure dropdown stays within viewport boundaries
    if (left + width > viewportWidth - 10) {
      left = viewportWidth - width - 10;
    }

    if (left < 10) {
      left = 10;
    }

    if (top + dropdownHeight > viewportHeight - 10) {
      top = viewportHeight - dropdownHeight - 10;
    }

    if (top < 10) {
      top = 10;
    }

    setDropdownPosition({
      top,
      left,
      width,
    });
  }, [options, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();

      const handleResize = () => calculateDropdownPosition();
      const handleScroll = () => calculateDropdownPosition();

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md outline-none cursor-pointer hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary dark:border-gray-700 dark:bg-darkCardBackground dark:text-gray-200"
      >
        <span className={!value ? "text-gray-400 dark:text-gray-500" : ""}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[999999] bg-white dark:bg-darkCardBackground border border-gray-200 dark:border-gray-700 rounded-md shadow-lg dark:shadow-gray-900/50"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            <div className="p-2 border-b dark:border-gray-700">
              <input
                type="text"
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary dark:border-gray-700 dark:bg-darkCardBackground dark:text-gray-200"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      value === option.value
                        ? "bg-primary/5 dark:bg-primary/10"
                        : ""
                    }`}
                    onClick={() => {
                      onChange(option.value);
                      closeDropdown();
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="dark:text-gray-200">{option.label}</span>
                      {value === option.value && (
                        <Check size={16} className="text-primary" />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const styles = `
  @keyframes gradient-x {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .animate-gradient-x {
    background-size: 200% 200%;
    animation: gradient-x 3s ease infinite;
  }

  @keyframes glow {
    0% { box-shadow: 0 0 5px rgba(var(--primary-rgb), 0.2), 0 0 10px rgba(var(--primary-rgb), 0.2), 0 0 15px rgba(var(--primary-rgb), 0.2); }
    50% { box-shadow: 0 0 20px rgba(var(--primary-rgb), 0.4), 0 0 30px rgba(var(--primary-rgb), 0.4), 0 0 40px rgba(var(--primary-rgb), 0.4); }
    100% { box-shadow: 0 0 5px rgba(var(--primary-rgb), 0.2), 0 0 10px rgba(var(--primary-rgb), 0.2), 0 0 15px rgba(var(--primary-rgb), 0.2); }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-glow {
    animation: glow 2s ease-in-out infinite;
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.5s ease-out forwards;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export const AddReportModal: React.FC<AddReportModalProps> = ({ onSubmit }) => {
  const _dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [useAI, setUseAI] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [reportOptions, setReportOptions] = useState<ReportOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchForms = async () => {
      setLoading(true);
      try {
        const forms = await getReportForms();
        const options = forms.map((form: Form) => ({
          id: form.id,
          label: form.label,
          value: form.key,
          fields: form.fields,
          acceptExtraFields: form.accept_extra_fields,
          aiGenerated: false,
        }));
        setReportOptions(options);
      } catch (error) {
        console.error("Failed to fetch report forms:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, []);

  const handleNext = () => {
    if (!selectedReport) return;
    const report = reportOptions.find((r) => r.value === selectedReport);
    if (report?.aiGenerated && useAI) {
      setIsNextLoading(true);
      setTimeout(() => {
        setIsNextLoading(false);
        setIsGenerating(true);
        setTimeout(() => {
          setIsGenerating(false);
          setStep(2);
        }, 5000);
      }, 500);
    } else {
      setStep(2);
    }
  };

  const handleStep2Submit = async (values: any) => {
    const selectedForm = reportOptions.find((r) => r.value === selectedReport);
    await onSubmit(
      {
        ...values,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        reportType: selectedReport,
        form_id: selectedForm?.id,
        useAI,
      },
      selectedForm?.id || 0,
    );
  };

  const renderReportForm = () => {
    if (!selectedReport) return null;
    const form = reportOptions.find((r) => r.value === selectedReport);
    if (!form?.fields?.length) return null;
    return (
      <DynamicFormRenderer
        fields={form.fields}
        acceptExtraFields={form.acceptExtraFields}
        onSubmit={handleStep2Submit}
        initialValues={useAI ? generateAIValues(selectedReport) : undefined}
        gridConfig={{ mobile: 1, desktop: form.fields.length > 1 ? 3 : 1 }}
      />
    );
  };

  const generateAIValues = (reportType: string | null) => {
    // This would be replaced with actual AI generation logic
    return {};
  };

  const renderStep1 = () => (
    <div className="pt-6 pb-4 px-6">
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Select Report Type <span className="text-red-500">*</span>
          </label>
          {loading ? (
            <div className="w-full">
              <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse mb-2" />
            </div>
          ) : (
            <SimpleSelect
              value={selectedReport || ""}
              onChange={setSelectedReport}
              options={reportOptions.map((r) => ({
                label: r.label,
                value: r.value,
              }))}
              placeholder={
                loading ? "Loading reports..." : "Select a report type"
              }
            />
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose the type of report to add
          </p>
        </div>

        <div
          className="h-[32px] transition-opacity duration-300"
          style={{
            opacity:
              selectedReport &&
              reportOptions.find((r) => r.value === selectedReport)?.aiGenerated
                ? 1
                : 0,
          }}
        >
          <div className="flex items-center">
            <input
              type="checkbox"
              id="useAI"
              checked={useAI}
              onChange={(e) => setUseAI(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary dark:bg-darkCardBackground"
              disabled={loading}
            />
            <label
              htmlFor="useAI"
              className="ml-2 block text-base font-semibold"
              style={{
                background:
                  "linear-gradient(to right, var(--primary), var(--purple-400), var(--primary-500), var(--purple-500), var(--primary-600), var(--purple-600))",
                WebkitTextFillColor: "transparent",
                WebkitBackgroundClip: "text",
                backgroundSize: "300% auto",
                animation: "gradient 4s linear infinite",
              }}
            >
              Generate using Genio AI
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          {loading ? (
            <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
          ) : (
            <button
              onClick={handleNext}
              disabled={!selectedReport || isNextLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 dark:focus:ring-offset-darkCardBackground"
            >
              {isNextLoading ? (
                <>
                  <div className="relative w-5 h-5 mr-2">
                    <div className="absolute inset-0 border-2 border-white/30 rounded-full" />
                    <div className="absolute inset-0 border-2 border-t-white rounded-full animate-spin" />
                  </div>
                  <span className="bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent animate-gradient-x">
                    Processing...
                  </span>
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderStepper = () => (
    <div
      className={`px-6 py-4 border-b dark:border-gray-700 ${isGenerating && "min-h-[80vh]"}`}
    >
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <div
            className={`relative flex items-center justify-center w-8 h-8 rounded-full ${
              step >= 1
                ? "bg-primary text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            }`}
          >
            {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : "1"}
          </div>
          <div
            className={`ml-2 text-sm font-medium ${
              step >= 1 ? "text-primary" : "text-gray-600 dark:text-gray-400"
            }`}
          >
            Select Report
          </div>
        </div>
        <div className="flex-1 h-0.5 mx-4 bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full bg-primary transition-all duration-300 ${
              step >= 2 ? "w-full" : "w-0"
            }`}
          />
        </div>
        <div className="flex items-center">
          <div
            className={`relative flex items-center justify-center w-8 h-8 rounded-full ${
              step >= 2
                ? "bg-primary text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            }`}
          >
            {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : "2"}
          </div>
          <div
            className={`ml-2 text-sm font-medium ${
              step >= 2 ? "text-primary" : "text-gray-600 dark:text-gray-400"
            }`}
          >
            Complete Details
          </div>
        </div>
      </div>
      {isGenerating && (
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="flex flex-col items-center gap-6">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary rounded-full opacity-20 blur-xl animate-glow" />
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={GenioLogo}
                  alt="Genio AI"
                  className="w-16 h-16 object-contain animate-pulse"
                />
              </div>
            </div>
            <div className="text-center space-y-2">
              <p className="text-xl font-semibold bg-gradient-to-r from-primary via-purple-500 to-primary bg-clip-text text-transparent animate-gradient-x">
                Crafting Your Report
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                Our AI is analyzing and generating your report with precision...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="px-6">
      <div className="animate-fade-in-up">
        {useAI && (
          <div className="mt-4 p-4 bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/5 dark:from-primary/20 dark:via-purple-500/20 dark:to-primary/10 rounded-lg border border-primary/20 dark:border-primary/30 animate-gradient-x">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Sparkles className="w-5 h-5 text-primary animate-pulse" />
                <div className="absolute inset-0 w-5 h-5 bg-primary/20 rounded-full animate-ping" />
              </div>
              <span className="font-medium bg-gradient-to-r from-primary via-purple-500 to-primary bg-clip-text text-transparent animate-gradient-x">
                Generated using Genio AI
              </span>
            </div>
          </div>
        )}
        {renderReportForm()}
      </div>
    </div>
  );

  const renderContent = () => (
    <div>
      {renderStepper()}
      {step === 1 ? renderStep1() : renderStep2()}
    </div>
  );

  return <div>{renderContent()}</div>;
};
